// Guidelines & SOP — an in-app reference for how OpsPanther's forecasting,
// routing, capacity and data model work. Plain content (no data fetching) so it
// loads instantly and stays in sync with the engine docs here.

import { useState } from "react";
import { Card } from "../../components/ui";

interface Sec {
  id: string;
  title: string;
  body: React.ReactNode;
}

const Code = ({ children }: { children: React.ReactNode }) => (
  <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[12px] text-slate-700">{children}</code>
);
const Term = ({ children }: { children: React.ReactNode }) => <span className="font-semibold text-slate-800">{children}</span>;
const P = ({ children }: { children: React.ReactNode }) => <p className="text-sm leading-relaxed text-slate-600">{children}</p>;
const UL = ({ children }: { children: React.ReactNode }) => <ul className="ml-5 list-disc space-y-1 text-sm leading-relaxed text-slate-600">{children}</ul>;

const SECTIONS: Sec[] = [
  {
    id: "overview",
    title: "1. Overview & glossary",
    body: (
      <>
        <P>
          OpsPanther forecasts parcel volume for a 3PL network and plans how that volume is sorted and moved. The flow is:{" "}
          <Term>customers</Term> inject volume into <Term>hubs</Term>; each hub sorts and ships to <Term>branches</Term>{" "}
          (last-mile depots); volume can move between hubs via <Term>linehaul</Term>.
        </P>
        <UL>
          <li><Term>Hub</Term> — a sort center. Has a daily capacity (= sum of its MHE limits) and a location (lat/lng).</li>
          <li><Term>Branch</Term> — a delivery depot that belongs to one <em>home hub</em>.</li>
          <li><Term>Customer</Term> — a shipper. Has a daily base, growth %, onboarding date, a DOW profile, and one or more hub <em>allocations</em>.</li>
          <li><Term>Lane</Term> — a customer→hub relationship: allocation %, load capacity, package mix, and a weekly linehaul schedule.</li>
          <li><Term>Operational week</Term> — Saturday → Friday.</li>
        </UL>
      </>
    ),
  },
  {
    id: "forecast",
    title: "2. The forecast formula",
    body: (
      <>
        <P>A customer's forecast at a hub on a date is:</P>
        <p className="my-2 rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-700">
          forecast = round( <b>dailyBase</b> × <b>growthFactor</b> × <b>allocation%</b> × <b>dayOfWeekFactor</b> × <b>seasonalMultiplier</b> )
        </p>
        <UL>
          <li><Term>growthFactor</Term> = compound annual <Code>growth%</Code> from the customer's onboarding date to the target date.</li>
          <li><Term>allocation%</Term> = the share of the customer's volume sent to that hub, for date-ranged allocations (must total 100% on any active date).</li>
          <li><Term>dayOfWeekFactor</Term> = the customer's DOW shape (a profile like standard / weekday / heavy-early, or a calibrated custom shape per hub).</li>
          <li><Term>seasonalMultiplier</Term> = the week-of-year curve, with holiday-table overrides.</li>
        </UL>
        <P>
          On top of the raw number, <Term>hub events</Term> scale/reroute the volume and manual <Term>overrides</Term> (from the
          Workbench) win over everything. A hub's own daily volume = the sum of its customers' forecasts.
        </P>
      </>
    ),
  },
  {
    id: "calibration",
    title: "3. Calibration",
    body: (
      <>
        <P>
          Calibration learns a customer's <Term>daily base</Term> and per-hub <Term>DOW factors</Term> from imported actuals,
          over a window of N complete Saturday→Friday weeks (2/3/4). DOW factors are stored <em>per hub lane</em> (a customer
          can ship a different weekday shape to each hub); the daily base is per customer.
        </P>
        <UL>
          <li><Term>Calibrate</Term> previews the learned base + DOW; <Term>Apply</Term> saves them; <Term>Reset</Term> reverts a lane to the customer's profile.</li>
          <li>The window flags data quality (no actuals / missing days / a hub event inside the window).</li>
        </UL>
      </>
    ),
  },
  {
    id: "events",
    title: "4. Hub events & reroutes",
    body: (
      <>
        <P>A hub event changes a hub's effective capacity for a date range, optionally redistributing displaced volume:</P>
        <UL>
          <li><Term>Closure</Term> = 0% capacity; <Term>Reduced</Term> / <Term>Expansion</Term> scale capacity by a %.</li>
          <li><Term>Reroute</Term> — the volume a hub can no longer handle (its reduction) is <Term>redistributed</Term> to other hubs by configured %s.</li>
        </UL>
        <P>
          The displaced volume leaves the source hub (<Code>reroutedOutOfHub</Code>) and is added to the destination hubs
          (<Code>reroutedInToHub</Code>), so the Forecast, Hub Dashboard, Event Impact and capacity views all reflect it. Reroute is a
          temporary, event-driven movement — distinct from the standing routing lanes below.
        </P>
      </>
    ),
  },
  {
    id: "routing",
    title: "5. Routing: Local / H2H / Direct",
    body: (
      <>
        <P>Every branch is fed by hubs in one of three ways (set on the Routing Map's <Term>Edit Lanes</Term>):</P>
        <UL>
          <li><Term>Local</Term> — the branch's home hub sorts it. The default; transit ≈ same-metro.</li>
          <li><Term>H2H</Term> — an origin hub ships to the branch's home hub (hub-to-hub), which re-sorts to the branch. Transit = origin → home-hub distance.</li>
          <li><Term>Direct</Term> — an origin hub ships <em>straight to the branch</em>, bypassing the home hub's sort. Transit = origin → branch distance.</li>
        </UL>
        <P>
          A lane carries a <Term>feed %</Term> (how much of the branch's volume comes via that origin) and an effective →
          revert date range. Changing a lane re-labels the routing and changes the transit estimate. Transit days come from
          great-circle distance bucketed (1d &lt; 300mi, 2d &lt; 700, 3d &lt; 1200, 4d &lt; 1800, else 5d).
        </P>
      </>
    ),
  },
  {
    id: "flow",
    title: "6. Volume flow: branches & hub throughput",
    body: (
      <>
        <P>
          <Term>Branch volume</Term> is downstream of hubs and is the <em>sum of every hub that feeds it</em>: its home hub
          (Local) plus H2H/Direct slices from other hubs. Each origin's slice = that hub's forecast × the branch's share of
          that origin (shares come from the seeded per-origin flow actuals).
        </P>
        <p className="my-2 rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-700">
          branchVolume(B) = Σ over origin hubs O of [ forecast(O) × share(O → B) ]
        </p>
        <P>
          <Term>Hub throughput</Term> = what a hub actually sorts:
        </P>
        <p className="my-2 rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-700">
          throughput(H) = own(H) − sentOut(H) + receivedIn(H)
        </p>
        <UL>
          <li><Term>own</Term> = volume originating at the hub (its customers' forecast).</li>
          <li><Term>sentOut</Term> = volume routed to other hubs' branches (H2H/Direct out).</li>
          <li><Term>receivedIn</Term> = volume routed in from other hubs for this hub's branches.</li>
        </UL>
        <P>
          Volume is conserved (Σ throughput = Σ own). The Hub Dashboard shows this breakdown, and the capacity/MHE checks use
          throughput — so a hub that nets H2H volume in is measured against what it really handles.
        </P>
      </>
    ),
  },
  {
    id: "trucks",
    title: "7. Truck / linehaul forecast & Volume Availability",
    body: (
      <>
        <P>
          Each lane has a weekly <Term>linehaul (LH) schedule</Term>: loads per weekday + arrival times (one time per load) +
          trailer type. <Code>impliedLoads</Code> = ceil(forecast ÷ load capacity) — the trucks a lane needs vs what's scheduled.
        </P>
        <P>The <Term>Volume Availability</Term> page models when volume lands across the day:</P>
        <UL>
          <li><Term>Scheduled</Term> (green) — volume arriving on the lane's scheduled loads at their arrival times.</li>
          <li><Term>Extra / unscheduled</Term> (amber) — forecast beyond what scheduled loads cover, spread as full-truck chunks every 2h across the sort window (start → cutoff).</li>
          <li><Term>Availability</Term> — the cumulative volume processed by each hour; surfaces over-forecast (needs more loads) and no-schedule lanes.</li>
        </UL>
      </>
    ),
  },
  {
    id: "capacity",
    title: "8. Capacity & MHE",
    body: (
      <>
        <P>
          Each hub has <Term>MHE (material-handling-equipment) sortation capacity</Term> by package type — Polybag, Cartons,
          Irregular (packages/day). The hub's daily capacity is the sum of the three.
        </P>
        <UL>
          <li>The <Term>MHE panel</Term> (Hub Dashboard) compares each type's busiest-day throughput vs its MHE limit → OK / Tight (&gt;85%) / Over (&gt;100%).</li>
          <li><Term>Capacity headroom</Term> (Event Impact) compares peak-day throughput (with events) vs the hub's capacity, and suggests reroutes to relieve over-capacity hubs.</li>
        </UL>
      </>
    ),
  },
  {
    id: "data",
    title: "9. Data sources",
    body: (
      <>
        <UL>
          <li><Term>Customer actuals</Term> — per customer/hub/day (with package split). Drive variance, MAPE, calibration, and the Hub Dashboard actuals.</li>
          <li><Term>Branch actuals</Term> — per branch/day; drive data-driven branch shares.</li>
          <li><Term>Flow actuals</Term> — per origin-hub/branch/day; drive the per-origin shares behind the network-flow branch model and hub throughput.</li>
          <li><Term>Overrides</Term> — manual per customer/hub/date values that win over the engine.</li>
        </UL>
        <P>
          Reads are paginated (Supabase caps a single response at 1,000 rows) and shares are aggregated server-side for speed.
        </P>
      </>
    ),
  },
];

export default function GuidelinesPage() {
  const [active, setActive] = useState(SECTIONS[0].id);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Guidelines &amp; SOP</h1>
        <p className="mt-1 text-sm text-slate-500">How OpsPanther's forecasting, routing, capacity and data model work.</p>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* TOC */}
        <nav className="lg:w-56 lg:shrink-0">
          <Card className="lg:sticky lg:top-4">
            <ul className="space-y-1 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    onClick={() => setActive(s.id)}
                    className={`block rounded-md px-2 py-1 transition ${active === s.id ? "bg-brand-50 font-medium text-brand-700" : "text-slate-600 hover:bg-slate-50"}`}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        </nav>

        {/* Sections */}
        <div className="min-w-0 flex-1 space-y-4">
          {SECTIONS.map((s) => (
            <Card key={s.id} className="scroll-mt-4" >
              <h2 id={s.id} className="mb-2 scroll-mt-4 text-base font-semibold text-slate-900">
                {s.title}
              </h2>
              <div className="space-y-2">{s.body}</div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
